<template>
    <b-modal
        ref="modalSaveReminder"
        title-tag="h2"
        :title="reminder ? 'Edit Reminder' : 'New Reminder'"
        lazy
    >
        <b-alert :show="showAlert" variant="warning">
            <i class="cus-error"></i>
            'Due' date has to be later than 'Given' date
        </b-alert>
        <b-alert :show="showReminderOptionsMessage" variant="dark">
            <ul class="mb-2 pl-3">
                <li
                    v-for="(action, index) in actionsNotAccept"
                    :key="'action' + index"
                >
                    {{ action.action_value }}
                </li>
            </ul>
            The action above for item <b>{{ item ? item.name : "" }}</b> cannot
            be applied here because this client
            <span v-if="typeOptions.length > 0"
                >has only opt for these reminder types :
                {{ getClientReminderOpt() }}
            </span>
            <span v-else>has not opt for any reminder types</span>
            .
        </b-alert>
        <div>
            <b-form-group
                v-if="reminder && reminder.client_id"
                label="Client:"
                label-cols="3"
                label-for="client"
            >
                <div class="p-2">
                    {{ reminder.client_name }}
                </div>
            </b-form-group>

            <b-form-group
                v-if="showPatient"
                label="Patient:"
                label-cols="3"
                label-for="patient"
            >
                <div class="p-2" v-if="reminder">
                    {{ reminder.patient_name }}
                </div>
                <v-select
                    v-else
                    id="patient"
                    ref="vselect"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('patient') }"
                    :options="patients"
                    v-model="selectedPatient"
                    label="name"
                    name="patient"
                    aria-placeholder="true"
                    aria-expanded="true"
                    placeholder="Patient name (required)"
                ></v-select>
            </b-form-group>

            <b-form-group label="Item:" label-cols="3" label-for="item">
                <div class="p-2" v-if="!isEmpty(reminder)">
                    <span v-if="reminder.item_name">{{
                        reminder.item_name
                    }}</span>
                    <span v-else-if="reminder.appointment_id">Appointment</span>
                    <span v-else-if="reminder.boarding_id">Boarding</span>
                    <span v-else>None selected</span>
                </div>
                <vet-select
                    v-else
                    id="item"
                    v-model="item"
                    :class="{
                        'border-danger': errors.has('item'),
                        rounded: true
                    }"
                    module="item"
                    placeholder="Item (optional)"
                    :selectProps="vetItemProps"
                    name="item"
                    @input="onItemChange"
                />
            </b-form-group>

            <b-form-group label-for="given-date" label="Given:" label-cols="3">
                <div
                    v-if="
                        isEmpty(reminder) ||
                        !isEmpty(reminder.item_id) ||
                        (isEmpty(reminder.appointment_id) &&
                            isEmpty(reminder.boarding_id))
                    "
                >
                    <b-form-input
                        v-if="checkIfDateReadOnly(reminder)"
                        :value="getGivenDateDisplay(reminder.record_date)"
                        name="readonly-date"
                        readonly
                    ></b-form-input>
                    <date-picker
                        v-else-if="checkRecordId(reminder)"
                        :config="givenOptions"
                        v-model="dateGiven"
                        name="given-date"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('given-date') }"
                        placeholder="Date Given (required)"
                        :readonly="!displayGiven"
                    ></date-picker>
                    <date-picker
                        v-else
                        :config="givenOptions"
                        v-model="dateGiven"
                        name="given-date"
                        placeholder="Date Given (optional)"
                    ></date-picker>
                </div>
                <div v-else class="p-2">None</div>
            </b-form-group>

            <b-form-group label-for="due-date1" label="Due:" label-cols="3">
                <date-picker
                    v-if="isEmpty(reminder) || isDueEditable(reminder)"
                    :config="dueOptions"
                    v-model="dateDue"
                    name="due-date1"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('due-date1') }"
                    placeholder="Date due (required)"
                ></date-picker>
                <div v-else class="p-2">{{ dateDue }}</div>
            </b-form-group>

            <b-form-group
                v-if="showReminder"
                label-for="itemActions"
                label="Reminders:"
                label-cols="3"
            >
                <v-select
                    id="reminder"
                    :options="itemActionsOptions"
                    v-model="itemActions"
                    placeholder="Reminder Option (optional)"
                    multiple
                    label="action_value"
                />
            </b-form-group>

            <b-form-group
                v-if="!showReminder"
                label-for="type"
                label="Type:"
                label-cols="3"
            >
                <b-form-select
                    v-model="reminderType"
                    value-field="value"
                    name="type"
                    class="text-capitalize"
                >
                    <b-form-select-option v-if="checkType(1)" :value="1"
                        >Email</b-form-select-option
                    >
                    <b-form-select-option v-if="checkType(2)" :value="2"
                        >Phone</b-form-select-option
                    >
                    <b-form-select-option v-if="checkType(3)" :value="3"
                        >Mail</b-form-select-option
                    >
                    <b-form-select-option v-if="checkType(5)" :value="5"
                        >SMS (Text)</b-form-select-option
                    >
                    <b-form-select-option :value="4"
                        >No Reminder</b-form-select-option
                    >
                </b-form-select>
            </b-form-group>

            <b-form-group
                v-if="!showReminder && (reminderType == 1 || reminderType == 5)"
                label-for="send"
                label="Send:"
                label-cols="3"
            >
                <div class="w-25 float-left">
                    <b-form-input
                        id="send"
                        v-model="reminderSend"
                        :class="{ 'border-danger': errors.has('send') }"
                        v-validate="'required'"
                        name="send"
                    ></b-form-input>
                </div>

                <div class="w-25 send-selections">
                    <b-form-select
                        v-model="timePeriod"
                        :options="timePeriodSelections"
                        value-field="value"
                        text-field="text"
                        name="time-period"
                    ></b-form-select>
                </div>

                <div class="w-50 send-selections">
                    <b-form-select
                        v-model="sendOption"
                        :options="sendOptionSelections"
                        value-field="value"
                        text-field="text"
                        name="send-option"
                    ></b-form-select>
                </div>
            </b-form-group>
            <b-form-group label-for="note" label="Notes:" label-cols="3">
                <editable
                    v-model="note"
                    placeholder="Note"
                    rows="3"
                    name="note"
                    :defaultHeight="64"
                ></editable>
            </b-form-group>
            <b-form-group
                v-if="reminder"
                label-for="applyToRecordGivenDate"
                label=""
                label-cols="3"
            >
                <b-form-checkbox
                    v-model="applyToRecordGivenDate"
                    name="applyToRecordGivenDate"
                    value="true"
                    unchecked-value="false"
                    plain
                    class="col-form-label"
                    >Apply To Record Given Date
                </b-form-checkbox>
            </b-form-group>
        </div>
        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!isLoading"
                @click="$refs.modalSaveReminder.hide()"
                class="float-left"
                variant="secondary"
                >Cancel</b-button
            >
            <div class="float-right">
                <loader v-if="isLoading" class="m-0" />
                <b-button
                    v-else-if="reminder"
                    @click="add(false)"
                    variant="primary"
                    >Save</b-button
                >
                <b-button-group v-else>
                    <b-dropdown
                        v-if="!continues"
                        @click="add(false)"
                        left
                        split
                        text="Save + Done"
                        variant="primary"
                    >
                        <b-dropdown-item @click="add(true)"
                            >Save + Add New</b-dropdown-item
                        >
                    </b-dropdown>
                    <b-dropdown
                        v-else
                        @click="add(true)"
                        left
                        split
                        text="Save + Add New"
                        variant="primary"
                    >
                        <b-dropdown-item @click="add(false)"
                            >Save + Done</b-dropdown-item
                        >
                    </b-dropdown>
                </b-button-group>
            </div>
        </div>
    </b-modal>
</template>
<style type="text/css" lang="scss">
.send-selections {
    padding-left: 7px;
    float: left;

    select {
        height: 30px;
        padding: 4px 6px;
    }
}
</style>
<script type="text/javascript">
import _ from "lodash";
import { DateTime } from "luxon";
import Patient from "@/services/patient";
import Reminders from "@/services/client-reminders";

export default {
    components: {
        vSelect: () => import("vue-select"),
        vetSelect: () => import("@/module/commons/vet-select.vue"),
        Loader: () => import("@/components/elements/loader"),
        Editable: () => import("@/components/elements/editable-textarea")
    },
    props: {
        clientId: {
            type: [String, Number],
            default: null
        },
        reminder: {
            type: Object,
            default: null
        },
        patientId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            selectedClient: null,
            selectedPatient: null,
            item: null,
            dateGiven: null,
            dateDue: null,
            reminderType: null,
            sendOption: 1,
            timePeriod: 4,
            reminderSend: 2,
            note: "",
            patients: [],
            items: [],
            typeOptions: [],
            timePeriodSelections: [
                { value: 4, text: "Day(s)" },
                { value: 5, text: "Week(s)" },
                { value: 6, text: "Month(s)" },
                { value: 7, text: "Year(s)" }
            ],
            sendOptionSelections: [
                { value: 1, text: "Before Due" },
                { value: 2, text: "After Due" }
            ],
            isRefining: false,

            givenOptions: {
                format: "DD/MM/YYYY"
            },
            dueOptions: {
                format: "DD/MM/YYYY"
            },
            defaultSelectedType: null,
            itemActions: [],
            itemActionsOptions: [],
            itemHasValue: 0,
            showAlert: false,
            continues: false,
            isLoading: false,
            displayGiven: true,
            applyToRecordGivenDate: true,
            inventoryDueRefs: [],

            vetItemProps: {
                id: "",
                item_type: "",
                module: "patient",
                status: "",
                start: "",
                image_source: ""
            },

            showReminderOptionsMessage: false,
            actionsNotAccept: []
        };
    },
    computed: {
        showReminder() {
            return this.itemActions.length > 0;
        },
        showPatient() {
            return _.isEmpty(this.patientId);
        },
        dueString() {
            return this.item ? this.item.due_date_instring : null;
        },
        theClient() {
            return this.clientId
                ? this.$store.getters["client/clientDetails"]
                : null;
        }
    },
    watch: {
        dateGiven(newValue, oldValue) {
            if (newValue && oldValue) {
                this.dateDue = this.setDueDate();
            }
        },
        dateDue(newValue) {
            if (newValue) {
                let given = DateTime.fromFormat(
                    this.dateGiven,
                    this._dateFormat
                );
                let due = DateTime.fromFormat(newValue, this._dateFormat);

                if (due.startOf("day") <= given.startOf("day")) {
                    this.showAlert = true;
                } else {
                    this.showAlert = false;
                }
            }
        }
    },
    methods: {
        open() {
            this.$refs.modalSaveReminder.show();

            this.givenOptions.format = this.getDateFormat();
            this.dueOptions.format = this.getDateFormat();

            this.$nextTick(() => {
                this.setSelectedClient();
                this.getType();

                if (this.showPatient && _.isEmpty(this.reminder)) {
                    this.getPatients();
                }

                if (this.reminder) {
                    this.setDetails(_.clone(this.reminder));
                    this.setDisplayGiven();
                    this.getInventoryDueOptions();
                } else {
                    this.dateGiven = this._now().toFormat(this._dateFormat);
                }
            });
        },
        async getInventoryDueOptions() {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/due`
            );

            if (res.data && res.data.response && res.data.response.resources) {
                this.inventoryDueRefs = res.data.response.resources;
            } else {
                this.inventoryDueRefs = [];
            }
        },
        setDetails(data) {
            this.dateGiven = data.start
                ? this._toDateTime(data.start).toFormat(this._dateFormat)
                : "";
            this.dateDue = data.due
                ? this._toDateTime(data.due).toFormat(this._dateFormat)
                : "";
            this.reminderType = data.reminder_type_id;
            this.reminderSend = data.reminder_send;
            this.timePeriod = data.time_period_id;
            this.sendOption = data.reminder_send_option_id;
            this.note = data.note;
        },
        setDueDate() {
            if (this.dateGiven) {
                if (this.dueString) {
                    let value = this.dueString.split(" ")[0];
                    let duration = this.dueString.split(" ")[1];

                    if (duration) {
                        if (duration.includes("day")) {
                            return DateTime.fromFormat(
                                this.dateGiven,
                                this._dateFormat
                            )
                                .plus({ days: value })
                                .toFormat(this._dateFormat);
                        } else if (duration.includes("week")) {
                            return DateTime.fromFormat(
                                this.dateGiven,
                                this._dateFormat
                            )
                                .plus({ weeks: value })
                                .toFormat(this._dateFormat);
                        } else if (duration.includes("month")) {
                            return DateTime.fromFormat(
                                this.dateGiven,
                                this._dateFormat
                            )
                                .plus({ months: value })
                                .toFormat(this._dateFormat);
                        }
                    }
                }

                // if still not return anything, then:
                // fix: Edit reminder -> due date is empty after changing Given date.
                if (this.reminder) {
                    let itemDueType = this.reminder.item_due;

                    if (itemDueType && itemDueType > 0) {
                        let dueInfo = this.inventoryDueRefs.find(
                            ref => ref.id === itemDueType
                        );

                        let durationSeconds = dueInfo ? dueInfo.value : 0;

                        return DateTime.fromFormat(
                            this.dateGiven,
                            this._dateFormat
                        )
                            .plus({ seconds: durationSeconds })
                            .toFormat(this._dateFormat);
                    } else {
                        return this.dateDue; // return as is
                    }
                }
            }

            return null;
        },
        setDisplayGiven() {
            let data = this.reminder;

            if (!_.isEmpty(data.record_id)) {
                if (data.record_item_id != data.item_id) {
                    this.displayGiven = false;
                }
            }
        },
        getItems(keyword) {
            this.isRefining = true;
            Reminders.getItems(keyword).then(response => {
                this.items = response;
                this.isRefining = false;
            });
        },
        onItemChange() {
            if (this.item) {
                this.dateDue = this.setDueDate();
                this.itemActions = [];
                this.itemActionsOptions = [];
                this.reminderSend = 2;
                Reminders.getItemActionOption(
                    this.item.id,
                    this.item.actions_key
                )
                    .then(response => {
                        this.itemHasValue = response.has_value;

                        if (this.itemHasValue) {
                            this.itemActions = response.reminder_options;
                            this.itemActionsOptions = response.reminder_options;
                        } else {
                            this.timePeriod = response.reminder_send_period;
                            this.sendOption = response.reminder_send_option;

                            if (this.checkType(response.reminder_type_id)) {
                                this.reminderType = response.reminder_type_id;
                            } else if (!_.isEmpty(this.typeOptions)) {
                                this.reminderType = this.typeOptions[0];
                            } else {
                                this.reminderType = 4;
                            }
                        }
                    })
                    .then(() => {
                        this.checkItemActionsReminderType();
                    });
            }
        },
        checkItemActionsReminderType() {
            // check if client/activeowner has the reminder option selected
            let self = this;
            self.showReminderOptionsMessage = false;
            self.actionsNotAccept = [];
            if (self.itemActions.length > 0) {
                self.itemActions = _.filter(self.itemActions, function (
                    action
                ) {
                    return self.checkType(action.reminder_type_id);
                });

                self.actionsNotAccept = _.filter(
                    self.itemActionsOptions,
                    function (action) {
                        return !self.checkType(action.reminder_type_id);
                    }
                );

                if (self.actionsNotAccept.length > 0) {
                    self.itemActionsOptions = self.itemActions;
                    if (self.clientId) self.showReminderOptionsMessage = true;
                }
            }
        },
        getClientReminderOpt() {
            let self = this;
            let reminderOptText = "";
            _.forEach(this.typeOptions, function (option, index) {
                let theText = _.findKey(self.REMINDER_TYPE, function (type) {
                    return type == option;
                });
                theText = theText ? _.upperFirst(theText.toLowerCase()) : "";
                reminderOptText += theText;
                reminderOptText +=
                    index != self.typeOptions.length - 1 ? ", " : "";
            });

            return reminderOptText;
        },
        // TODO : Check this func in use or not
        refineSearch(keyword) {
            this.$refs.vselectitem.open = false;
            if (keyword.length > 2) {
                this.$refs.vselectitem.open = true;
                this.getItems(keyword);
            } else {
                this.$emit("search:blur");
            }
        },
        getPatients() {
            let params = {
                search_key: null,
                id: this.clientId,
                all_status: 1
            };
            Patient.getPatientsByClient(params).then(response => {
                this.patients = response;
            });
        },
        checkType(typeId) {
            let type = _.find(this.typeOptions, function (o) {
                return Number(o) === Number(typeId);
            });
            return typeof type !== "undefined";
        },
        getType() {
            let self = this;
            let prevSelection = this.reminder
                ? this.reminder.reminder_type_id
                : 4;

            if (_.isEmpty(this.patientId)) {
                Reminders.getReminderType(this.clientId, prevSelection).then(
                    response => {
                        self.typeOptions = response.selected_options;
                        self.defaultSelectedType = response.selected_type;

                        if (!_.isEmpty(response.selected_options)) {
                            if (self.reminder) {
                                self.reminderType =
                                    self.reminder.reminder_type_id;
                            } else {
                                self.reminderType =
                                    response.selected_options[0];
                            }
                        } else {
                            self.reminderType = response.selected_type || 4;
                        }
                    }
                );
            } else {
                Reminders.filterReminderTypeByPatient(this.patientId).then(
                    response => {
                        self.typeOptions = response.resources.selected_options;
                        self.defaultSelectedType =
                            response.resources.selected_type;

                        if (!_.isEmpty(response.resources.selected_options)) {
                            if (self.reminder) {
                                self.reminderType =
                                    self.reminder.reminder_type_id;
                            } else {
                                self.reminderType =
                                    response.resources.selected_options[0];
                            }
                        } else {
                            self.reminderType =
                                response.resources.selected_type || 4;
                        }
                    }
                );
            }
        },
        showError(msg) {
            let defaultMessage = "Error : Unable to save reminder";
            this._message(msg || defaultMessage, "danger");
            this.isLoading = false;

            // this.$refs.modalSaveReminder.hide();
        },
        async add(continues) {
            let valid = await this.$validator.validateAll();
            if (!valid || this.showAlert) {
                return false;
            }

            this.continues = continues;
            this.isLoading = true;
            let pid = this.patientId ? this.patientId : null;

            if (this.reminder) {
                pid = this.reminder.patient_id;
            } else if (_.isEmpty(this.patientId)) {
                pid = this.selectedPatient.id;
            }

            let params = {
                reminder_id: this.reminder ? this.reminder.reminder_id : "",
                reminder_actor_id: this.reminder
                    ? this.reminder.reminder_actor_id
                    : "",

                cid: this.selectedClient,
                pid,

                itemid: this.item
                    ? this.item.id
                    : this.reminder
                    ? this.reminder.item_id
                    : "",

                record_id: this.reminder ? this.reminder.record_id : "",
                appointment_id: this.reminder
                    ? this.reminder.appointment_id
                    : "",
                boarding_id: this.boarding_id ? this.reminder.boarding_id : "",
                due: this.dateDue,

                reminder_send: this.reminderSend,
                reminder_time_period: this.timePeriod,
                reminder_send_option: this.sendOption,
                reminder_type: this.reminderType,

                note: this.note,
                has_actions: this.itemActions.length > 0 ? 1 : 0,

                start: this.dateGiven,
                start_time: this._now().toFormat("h:mm a"),

                module: this.patientId ? "patient" : "client",
                reminder_message: this.reminder ? this.reminder.content : "",

                applyToRecordGivenDate: this.applyToRecordGivenDate
            };

            let formData = new FormData();

            _.forEach(params, function (value, key) {
                if (value) {
                    formData.append(key, value);
                } else {
                    formData.append(key, "");
                }
            });

            _.forEach(this.itemActions, function (value) {
                formData.append("iitem_actions[]", value.action_id);
            });

            Reminders.saveReminder(formData)
                .then(response => {
                    this.isLoading = false;
                    if (response.state === 0) {
                        this._message(response.message);
                        this.$emit("reload");

                        if (continues) {
                            this.resetData();
                        } else {
                            this.$refs.modalSaveReminder.hide();
                        }
                    } else {
                        throw new Error(response.message);
                    }
                })
                .catch(error => {
                    this.showError(error.message);
                    console.error(error);
                });
        },
        resetData() {
            this.reminderType = null;
            this.reminderSend = 2;
            this.sendOption = 1;
            this.timePeriod = 4;
            this.dateDue = null;
            this.note = null;
            this.item = null;
            this.showReminder = false;
            this.itemActions = [];
            this.itemActionsOptions = [];

            if (_.isEmpty(this.patientId)) {
                this.selectedPatient = null;
            }

            this.open();
            this.$validator.reset();
        },
        isEmpty(data) {
            return _.isEmpty(data);
        },
        checkRecordId(reminder) {
            if (reminder && !_.isEmpty(reminder.record_id)) {
                return true;
            }

            return false;
        },
        isDueEditable(reminder) {
            if (reminder) {
                if (
                    reminder.item_id ||
                    (_.isEmpty(reminder.appointment_id) &&
                        _.isEmpty(reminder.boarding_id))
                ) {
                    return true;
                }
            }

            return false;
        },
        setSelectedClient() {
            if (this.patientId) {
                if (this.reminder) {
                    this.selectedClient = this.reminder.client_id;
                }
            } else {
                this.selectedClient = this.clientId ? this.clientId : null;
            }
        },
        getGivenDateDisplay(recordDate) {
            return recordDate ? this._setDateDisplay(recordDate) : null;
        },
        checkIfDateReadOnly(reminder) {
            return (
                reminder &&
                reminder.record_id &&
                reminder.record_item_id != reminder.item_id
            );
        }
    }
};
</script>
